import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlarmClock, BarChart3, Cake, Check, Dices, Gift, Lightbulb, Moon, Plus, Settings2, Sparkles, Square, Star, ThumbsDown, ThumbsUp, Trash2, X } from "lucide-react";
import { botDelete, botGet, botPatch, botPost, botPut, apiError } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChannelSelect, RoleSelect } from "@/components/pickers";
import {
  ChannelMulti, Empty, FeatureGate, Field, Loading, MemberSearch, MessageMini, NumberInput, PageHeader, Panel, Pill, PillTabs, SaveBar,
  SimpleSelect, TextArea, TextInput, ToggleRow, cleanMessage, formatDateTime, outlineBtn, primaryBtn, timeAgo, useFeature,
} from "@/components/kit";

// ─────────────────────────── Giveaway e sondaggi ───────────────────────────
const DURATIONS = [[30, "30 minuti"], [60, "1 ora"], [360, "6 ore"], [1440, "1 giorno"], [4320, "3 giorni"], [10080, "1 settimana"], [43200, "30 giorni"]];

export function Giveaways() {
  const [tab, setTab] = useState("giveaways");
  return (
    <div className="space-y-6">
      <PageHeader icon={Gift} title="Giveaway e sondaggi" subtitle="Premi con estrazione automatica e sondaggi nativi di Discord." />
      <PillTabs active={tab} onChange={setTab} tabs={[{ id: "giveaways", label: "Giveaway", icon: Gift }, { id: "polls", label: "Sondaggi", icon: BarChart3 }]} />
      {tab === "giveaways" ? <GiveawayList /> : <PollForm />}
    </div>
  );
}

function GiveawayCard({ g, channelName, act }) {
  return (
    <div className={`relative overflow-hidden rounded-2xl border p-5 ${g.ended ? "border-[#232838] bg-[#161923]" : "border-[#FEE75C]/25 bg-gradient-to-br from-[#FEE75C]/10 via-[#161923] to-[#161923]"}`}>
      <Gift className="absolute -right-4 -top-4 h-24 w-24 text-white/5" />
      <div className="flex flex-wrap items-center gap-2"><p className="text-lg font-extrabold text-white">{g.prize}</p><Pill tone={g.ended ? "gray" : "green"}>{g.ended ? "Terminato" : "In corso"}</Pill></div>
      <p className="mt-1 text-xs text-[#949BA4]">#{channelName(g.channelId)} · {g.entries.length} partecipanti · {g.winners} vincitori · {g.ended ? `terminato ${timeAgo(new Date(g.endsAt).toISOString())}` : `termina il ${formatDateTime(new Date(g.endsAt).toISOString())}`}</p>
      {g.ended && <p className="mt-2 text-sm text-[#DBDEE1]">🏆 {g.winnerIds.length ? `${g.winnerIds.length} vincitori estratti` : "Nessun vincitore"}</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        {!g.ended && <Button size="sm" onClick={() => act(() => botPost(`giveaways/${g.id}/end`), "Giveaway terminato")} className={primaryBtn}><Square className="mr-1.5 h-3.5 w-3.5" />Termina ora</Button>}
        {g.ended && <Button size="sm" onClick={() => act(() => botPost(`giveaways/${g.id}/reroll`), "Nuovo vincitore estratto")} className={primaryBtn}><Dices className="mr-1.5 h-3.5 w-3.5" />Riestrai</Button>}
        <Button size="sm" variant="outline" onClick={() => act(() => botDelete(`giveaways/${g.id}`), "Giveaway eliminato")} className={outlineBtn}><Trash2 className="mr-1.5 h-3.5 w-3.5" />Elimina</Button>
      </div>
    </div>
  );
}

function GiveawayList() {
  const { channelName } = useData();
  const [list, setList] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(() => botGet("giveaways").then(setList).catch((e) => { toast.error(apiError(e)); setList([]); }), []);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(apiError(e)); } };
  if (!list) return <Loading />;
  return (
    <div className="space-y-4">
      <Button onClick={() => setCreating(true)} className={primaryBtn} data-testid="new-giveaway"><Plus className="mr-2 h-4 w-4" />Nuovo giveaway</Button>
      {list.length === 0 && <Empty icon={Gift} title="Nessun giveaway">I membri partecipano con un bottone e il bot estrae da solo.</Empty>}
      <div className="grid gap-4 md:grid-cols-2">{list.map((g) => <GiveawayCard key={g.id} g={g} channelName={channelName} act={act} />)}</div>
      <NewGiveaway open={creating} onClose={() => setCreating(false)} onDone={load} />
    </div>
  );
}

function NewGiveaway({ open, onClose, onDone }) {
  const [form, setForm] = useState({ channelId: "", prize: "", description: "", winners: 1, durationMinutes: 1440, requiredRoleId: "" });
  const set = (p) => setForm((f) => ({ ...f, ...p }));
  const submit = async () => {
    try {
      await botPost("giveaways", { ...form, description: form.description || undefined, requiredRoleId: form.requiredRoleId || null });
      toast.success("Giveaway avviato", { description: form.prize }); onDone(); onClose(); set({ prize: "", description: "" });
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-[#232838] bg-[#161923] text-[#DBDEE1]">
        <DialogHeader><DialogTitle className="text-white">Nuovo giveaway</DialogTitle><DialogDescription className="text-[#949BA4]">Il messaggio con il bottone "Partecipa" viene pubblicato subito.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Field label="Premio"><TextInput value={form.prize} onChange={(prize) => set({ prize })} placeholder="es. Nitro 1 mese" /></Field>
          <Field label="Descrizione (facoltativa)"><TextArea value={form.description} onChange={(description) => set({ description })} rows={2} /></Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Canale"><ChannelSelect value={form.channelId} onChange={(channelId) => set({ channelId })} /></Field>
            <Field label="Durata"><SimpleSelect value={form.durationMinutes} onChange={(v) => set({ durationMinutes: Number(v) })} options={DURATIONS.map(([v, l]) => ({ value: v, label: l }))} /></Field>
            <Field label="Vincitori"><NumberInput value={form.winners} min={1} max={50} onChange={(winners) => set({ winners })} /></Field>
            <Field label="Ruolo richiesto (facoltativo)"><RoleSelect value={form.requiredRoleId} onChange={(requiredRoleId) => set({ requiredRoleId })} placeholder="Tutti" /></Field>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={!form.channelId || !form.prize.trim()} className={primaryBtn}><Gift className="mr-2 h-4 w-4" />Avvia giveaway</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PollForm() {
  const empty = () => ({ channelId: "", question: "", answers: [{ text: "", emoji: "" }, { text: "", emoji: "" }], hours: 24, multiselect: false });
  const [poll, setPoll] = useState(empty);
  const set = (p) => setPoll((x) => ({ ...x, ...p }));
  const answers = poll.answers.filter((a) => a.text.trim());
  const setAnswer = (i, patch) => set({ answers: poll.answers.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  const submit = async () => {
    try {
      await botPost("polls", { ...poll, answers: answers.map((a) => ({ text: a.text.trim(), emoji: a.emoji.trim() || undefined })) });
      toast.success("Sondaggio pubblicato"); setPoll((p) => ({ ...empty(), channelId: p.channelId }));
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Panel title="Nuovo sondaggio" desc="Pubblicato come sondaggio nativo di Discord: i risultati si vedono nel messaggio." icon={BarChart3}>
      <div className="space-y-4">
        <Field label="Domanda"><TextInput value={poll.question} onChange={(question) => set({ question })} maxLength={300} placeholder="es. Quale evento facciamo sabato?" /></Field>
        <div className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-wide text-[#B5BAC1]">Risposte ({poll.answers.length}/10)</p>
          {poll.answers.map((a, i) => (
            <div key={i} className="flex gap-2">
              <div className="w-16 shrink-0"><TextInput value={a.emoji} onChange={(emoji) => setAnswer(i, { emoji })} placeholder="🙂" /></div>
              <TextInput value={a.text} onChange={(text) => setAnswer(i, { text })} maxLength={55} placeholder={`Risposta ${i + 1}`} />
              <Button size="icon" variant="ghost" disabled={poll.answers.length <= 2} onClick={() => set({ answers: poll.answers.filter((_, j) => j !== i) })} className="text-[#949BA4] hover:text-white"><X className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" disabled={poll.answers.length >= 10} onClick={() => set({ answers: [...poll.answers, { text: "", emoji: "" }] })} className={outlineBtn}><Plus className="mr-1 h-4 w-4" />Risposta</Button>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Canale"><ChannelSelect value={poll.channelId} onChange={(channelId) => set({ channelId })} /></Field>
          <Field label="Durata"><NumberInput value={poll.hours} min={1} max={768} suffix="ore" onChange={(hours) => set({ hours })} /></Field>
          <Field label="Scelta multipla"><div className="flex h-10 items-center gap-2"><Switch checked={poll.multiselect} onCheckedChange={(multiselect) => set({ multiselect })} /><span className="text-sm text-[#949BA4]">Più risposte</span></div></Field>
        </div>
        <Button onClick={submit} disabled={!poll.channelId || !poll.question.trim() || answers.length < 2} className={primaryBtn}>Pubblica sondaggio</Button>
      </div>
    </Panel>
  );
}

// ─────────────────────────── Suggerimenti ───────────────────────────
const STATUS = { pending: ["In attesa", "blue"], approved: ["Approvato", "green"], denied: ["Rifiutato", "red"], implemented: ["Realizzato", "yellow"] };

export function Suggestions() {
  const [tab, setTab] = useState("list");
  return (
    <div className="space-y-6">
      <PageHeader icon={Lightbulb} title="Suggerimenti" subtitle="I membri propongono idee con /suggest, la community vota, tu decidi." />
      <PillTabs active={tab} onChange={setTab} tabs={[{ id: "list", label: "Suggerimenti", icon: Lightbulb }, { id: "settings", label: "Impostazioni", icon: Settings2 }]} />
      {tab === "list" ? <SuggestionList /> : <SuggestionSettings />}
    </div>
  );
}

function SuggestionList() {
  const [status, setStatus] = useState("pending");
  const [list, setList] = useState(null);
  const [review, setReview] = useState(null);
  const [note, setNote] = useState("");
  const load = useCallback(() => botGet("suggestions", status === "all" ? undefined : { status }).then(setList).catch(() => setList([])), [status]);
  useEffect(() => { load(); }, [load]);
  const decide = async () => {
    try { await botPatch(`suggestions/${review.item.id}`, { status: review.status, note: note.trim() || null }); toast.success(`Suggerimento #${review.item.id}: ${STATUS[review.status][0].toLowerCase()}`); setReview(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const open = (item, st) => { setReview({ item, status: st }); setNote(item.reviewNote ?? ""); };
  return (
    <div className="space-y-4">
      <PillTabs active={status} onChange={setStatus} tabs={[["pending", "In attesa"], ["approved", "Approvati"], ["implemented", "Realizzati"], ["denied", "Rifiutati"], ["all", "Tutti"]].map(([id, label]) => ({ id, label }))} />
      {!list ? <Loading /> : list.length === 0 ? <Empty icon={Lightbulb} title="Nessun suggerimento qui" /> : list.map((s) => (
        <Panel key={s.id}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm font-bold text-[#8ea0ff]">#{s.id}</span>
            <Pill tone={STATUS[s.status][1]}>{STATUS[s.status][0]}</Pill>
            <span className="text-xs text-[#949BA4]">da {s.anonymous ? "anonimo" : s.userTag} · {timeAgo(s.createdAt)}</span>
            <span className="ml-auto flex gap-3 text-sm"><span className="flex items-center gap-1 text-[#57d38c]"><ThumbsUp className="h-4 w-4" />{s.up.length}</span><span className="flex items-center gap-1 text-[#ff7a7d]"><ThumbsDown className="h-4 w-4" />{s.down.length}</span></span>
          </div>
          <p className="mt-3 whitespace-pre-wrap text-[#DBDEE1]">{s.text}</p>
          {s.reviewNote && <p className="mt-2 rounded-lg bg-[#131620] p-2 text-sm text-[#949BA4]">💬 {s.reviewNote}</p>}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button size="sm" onClick={() => open(s, "approved")} className="bg-[#23A55A] text-white hover:bg-[#1a8b4a]"><Check className="mr-1 h-4 w-4" />Approva</Button>
            <Button size="sm" variant="outline" onClick={() => open(s, "denied")} className="border-[#F23F43]/40 bg-transparent text-[#ff7a7d] hover:bg-[#F23F43]/10"><X className="mr-1 h-4 w-4" />Rifiuta</Button>
            <Button size="sm" variant="outline" onClick={() => open(s, "implemented")} className={outlineBtn}><Sparkles className="mr-1 h-4 w-4" />Realizzato</Button>
            <Button size="sm" variant="ghost" onClick={async () => { try { await botDelete(`suggestions/${s.id}`); load(); } catch (e) { toast.error(apiError(e)); } }} className="ml-auto text-[#F23F43] hover:bg-[#F23F43]/10"><Trash2 className="h-4 w-4" /></Button>
          </div>
        </Panel>
      ))}
      <Dialog open={!!review} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent className="border-[#232838] bg-[#161923] text-[#DBDEE1]">
          <DialogHeader><DialogTitle className="text-white">{review && `${STATUS[review.status][0]} · #${review.item.id}`}</DialogTitle><DialogDescription className="text-[#949BA4]">La risposta viene mostrata sotto al suggerimento su Discord.</DialogDescription></DialogHeader>
          <Field label="Risposta dello staff (facoltativa)"><TextArea value={note} onChange={setNote} rows={3} /></Field>
          <DialogFooter><Button onClick={decide} className={primaryBtn}>Conferma</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SuggestionSettings() {
  const f = useFeature("suggestions");
  return (
    <FeatureGate feature={f}>
      {(v) => (
        <div className="space-y-3">
          <ToggleRow title={v.enabled ? "Suggerimenti attivi" : "Suggerimenti disattivati"} desc="Abilita il comando /suggest." checked={v.enabled} onChange={(enabled) => f.change({ enabled })} />
          <Panel><Field label="Canale dei suggerimenti"><ChannelSelect value={v.channelId} onChange={(channelId) => f.change({ channelId })} /></Field></Panel>
          <ToggleRow title="Crea una discussione" desc="Apre un thread sotto ogni suggerimento." checked={v.createThread} onChange={(createThread) => f.change({ createThread })} />
          <ToggleRow title="Suggerimenti anonimi" desc="Non mostra chi ha proposto l'idea." checked={v.anonymous} onChange={(anonymous) => f.change({ anonymous })} />
          <SaveBar feature={f} />
        </div>
      )}
    </FeatureGate>
  );
}

// ─────────────────────────── Compleanni, starboard, AFK, promemoria ───────────────────────────
const MONTHS = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];

export function CommunityExtras() {
  const [tab, setTab] = useState("birthdays");
  return (
    <div className="space-y-6">
      <PageHeader icon={Cake} title="Compleanni e altro" subtitle="Compleanni, starboard, AFK e promemoria." />
      <PillTabs active={tab} onChange={setTab} tabs={[{ id: "birthdays", label: "Compleanni", icon: Cake }, { id: "starboard", label: "Starboard", icon: Star }, { id: "afk", label: "AFK", icon: Moon }, { id: "reminders", label: "Promemoria", icon: AlarmClock }]} />
      {tab === "birthdays" && <Birthdays />}
      {tab === "starboard" && <Starboard />}
      {tab === "afk" && <SimpleToggles name="afk" title="AFK" desc="Con /afk un membro segnala che è assente; chi lo menziona riceve un avviso."
        extra={(v, f) => <ToggleRow title="Aggiungi [AFK] al nickname" checked={v.nicknamePrefix} onChange={(nicknamePrefix) => f.change({ nicknamePrefix })} />} />}
      {tab === "reminders" && <SimpleToggles name="reminders" title="Promemoria" desc="Con /remind i membri si fanno ricordare qualcosa dal bot."
        extra={(v, f) => <Panel><div className="flex items-center gap-2 text-sm text-[#DBDEE1]">Massimo <NumberInput value={v.maxPerUser} min={1} max={100} className="w-20" suffix="promemoria attivi per persona" onChange={(maxPerUser) => f.change({ maxPerUser })} /></div></Panel>} />}
    </div>
  );
}

function SimpleToggles({ name, title, desc, extra }) {
  const f = useFeature(name);
  return (
    <FeatureGate feature={f}>
      {(v) => (
        <div className="space-y-3">
          <ToggleRow title={`${title} ${v.enabled ? "attivo" : "disattivato"}`} desc={desc} checked={v.enabled} onChange={(enabled) => f.change({ enabled })} />
          {extra(v, f)}
          <SaveBar feature={f} />
        </div>
      )}
    </FeatureGate>
  );
}

function Birthdays() {
  const f = useFeature("birthdays");
  const [list, setList] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ member: null, day: 1, month: 1 });
  const load = useCallback(() => botGet("birthdays").then(setList).catch(() => setList([])), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    try { await botPut(`birthdays/${form.member.id}`, { day: form.day, month: form.month }); toast.success("Compleanno salvato"); setAdding(false); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4">
      <FeatureGate feature={f}>
        {(v) => (
          <div className="space-y-3">
            <ToggleRow title={v.enabled ? "Auguri automatici attivi" : "Auguri automatici disattivati"} desc="All'ora scelta il bot fa gli auguri a chi compie gli anni." checked={v.enabled} onChange={(enabled) => f.change({ enabled })} />
            <Panel>
              <div className="grid gap-4 md:grid-cols-3">
                <Field label="Canale degli auguri"><ChannelSelect value={v.channelId} onChange={(channelId) => f.change({ channelId })} /></Field>
                <Field label="Ruolo del festeggiato (1 giorno)"><RoleSelect value={v.roleId} onChange={(roleId) => f.change({ roleId })} /></Field>
                <Field label="Ora degli auguri"><NumberInput value={v.hour} min={0} max={23} suffix=":00" onChange={(hour) => f.change({ hour })} /></Field>
              </div>
              <div className="mt-4"><MessageMini value={v.message} onChange={(message) => f.change({ message })} /></div>
            </Panel>
            <SaveBar feature={f} prepare={(x) => ({ ...x, message: cleanMessage(x.message) })} />
          </div>
        )}
      </FeatureGate>
      <Panel title="Prossimi compleanni" icon={Cake} right={<Button size="sm" onClick={() => setAdding(true)} className={primaryBtn}><Plus className="mr-1 h-4 w-4" />Aggiungi</Button>}>
        {!list ? <Loading /> : list.length === 0 ? <Empty icon={Cake} title="Nessun compleanno salvato">I membri lo impostano con /birthday set.</Empty> : (
          <div className="grid gap-2 sm:grid-cols-2">
            {list.map((b) => (
              <div key={b.userId} className="flex items-center gap-3 rounded-xl border border-[#232838] bg-[#131620] px-3 py-2.5">
                <span className="text-2xl">🎂</span>
                <div className="min-w-0 flex-1"><p className="font-semibold text-white">{b.username}</p><p className="text-xs text-[#949BA4]">{b.day} {MONTHS[b.month - 1]}</p></div>
                <Button size="icon" variant="ghost" onClick={async () => { await botDelete(`birthdays/${b.userId}`).catch(() => {}); load(); }} className="text-[#949BA4] hover:text-[#F23F43]"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </Panel>
      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="border-[#232838] bg-[#161923] text-[#DBDEE1]">
          <DialogHeader><DialogTitle className="text-white">Aggiungi compleanno</DialogTitle><DialogDescription className="text-[#949BA4]">Il bot farà gli auguri in automatico.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Field label="Membro"><MemberSearch value={form.member} onChange={(member) => setForm((x) => ({ ...x, member }))} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Giorno"><NumberInput value={form.day} min={1} max={31} onChange={(day) => setForm((x) => ({ ...x, day }))} /></Field>
              <Field label="Mese"><SimpleSelect value={form.month} onChange={(v) => setForm((x) => ({ ...x, month: Number(v) }))} options={MONTHS.map((m, i) => ({ value: i + 1, label: m }))} /></Field>
            </div>
          </div>
          <DialogFooter><Button onClick={add} disabled={!form.member} className={primaryBtn}>Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Starboard() {
  const f = useFeature("starboard");
  return (
    <FeatureGate feature={f}>
      {(v) => (
        <div className="space-y-3">
          <ToggleRow title={v.enabled ? "Starboard attiva" : "Starboard disattivata"} desc="I messaggi con abbastanza reazioni finiscono in un canale 'hall of fame'." checked={v.enabled} onChange={(enabled) => f.change({ enabled })} />
          <Panel>
            <div className="grid gap-4 md:grid-cols-3">
              <Field label="Canale starboard"><ChannelSelect value={v.channelId} onChange={(channelId) => f.change({ channelId })} /></Field>
              <Field label="Emoji"><TextInput value={v.emoji} onChange={(emoji) => f.change({ emoji })} maxLength={60} /></Field>
              <Field label="Reazioni necessarie"><NumberInput value={v.threshold} min={1} max={100} onChange={(threshold) => f.change({ threshold })} /></Field>
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <ToggleRow title="Conta la reazione dell'autore" checked={v.selfStar} onChange={(selfStar) => f.change({ selfStar })} />
              <Field label="Canali esclusi"><ChannelMulti value={v.ignoredChannelIds} onChange={(ignoredChannelIds) => f.change({ ignoredChannelIds })} /></Field>
            </div>
          </Panel>
          <SaveBar feature={f} />
        </div>
      )}
    </FeatureGate>
  );
}
